/**
 * usePasswordPolicy (#623)
 *
 * Fetches the live, admin-configurable password policy from the public
 * `GET /users/settings/password-policy` endpoint and derives the requirement
 * checklist from it. This keeps every password-entry surface (account
 * completion, change/set-password modals, etc.) showing and enforcing exactly
 * what an admin configured in System Settings — instead of a hardcoded rule.
 *
 * Before the request resolves (or if it fails) it falls back to
 * DEFAULT_FRONTEND_POLICY (8 chars, no character classes), so the UI is always
 * usable and never stricter than the server actually enforces. React Query
 * dedupes the shared `['password-policy']` key, so multiple components calling
 * this hook trigger a single network request.
 */

import { useQuery } from '@tanstack/react-query'
import { getPasswordPolicy } from '@/services/api/settings.api'
import {
  buildPasswordRequirements,
  DEFAULT_FRONTEND_POLICY,
  PasswordPolicyShape,
  PasswordRequirement,
  PasswordUserInfo,
} from '@/utils/passwordValidation'

export interface UsePasswordPolicyResult {
  policy: PasswordPolicyShape
  requirements: PasswordRequirement[]
  isLoading: boolean
}

/**
 * @param userInfo Optional logged-in user identity (email/first/last name) used
 *   to enforce the always-on `preventUserInfo` rule live, client-side. Callers
 *   that have the current user (e.g. PasswordChangeModal via useAuth) should
 *   pass it so the checklist can fail BEFORE the API rejects the password.
 */
export const usePasswordPolicy = (userInfo?: PasswordUserInfo): UsePasswordPolicyResult => {
  const { data, isLoading } = useQuery({
    queryKey: ['password-policy'],
    queryFn: async (): Promise<PasswordPolicyShape | null> => {
      const res = await getPasswordPolicy()
      return res.success && res.data ? (res.data as PasswordPolicyShape) : null
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const policy = data ?? DEFAULT_FRONTEND_POLICY
  return {
    policy,
    requirements: buildPasswordRequirements(policy, userInfo),
    isLoading,
  }
}
